/**
 * client/src/lib/bankStatementUiReadiness.ts
 *
 * PHASE 14C / 14C.2 — Bank-statement UI readiness TIERS (presentation logic only).
 *
 * The bank pipeline separates three readiness states
 * (summaryMetricsReady / transactionAnalyticsReady / runningBalanceAnalyticsReady).
 * As of Phase 14C.2 those authoritative states are PERSISTED to
 * `bank_statements.bankReadinessJson` (on the resolved-candidate path) and threaded
 * to the frontend, so the adapter prefers backend truth when present. Records
 * extracted before 14C.2 (legacy/shadow path) have null metadata; for them the
 * frontend reconstructs readiness from `needsReview`, `transactionExtractionFailed`
 * and the numeric summary fields (the legacy balance-equation inference).
 *
 * This module is a PURE, presentation-layer mapping. It does TWO things:
 *
 *   1. `deriveBankStatementUiReadiness` — the canonical helper. Given the readiness
 *      states (plus needsReview and an average-balance provenance flag) it returns
 *      the UI tier, label, message, per-surface show flags, and the decision source.
 *
 *   2. `deriveReadinessInputFromStatement` — an adapter that uses persisted backend
 *      readiness metadata VERBATIM when present (source="backend"), and otherwise
 *      reconstructs the states from the fields the frontend has (source=
 *      "legacy_inferred") — without any backend validation / extraction change.
 *
 * It NEVER mutates backend data, never changes `needsReview` semantics, and never
 * estimates an average balance from opening/ending balances.
 */

export type ReadinessState = "ready" | "blocked" | "not_available" | "unknown";

/**
 * Where the UI tier was decided from:
 *   - "backend"         — persisted backend readiness metadata was present and used.
 *   - "legacy_inferred" — no backend metadata (old record); inferred from
 *                         needsReview + the printed balance equation.
 */
export type BankStatementReadinessSource = "backend" | "legacy_inferred" | "dealer_verified";

export type BankStatementUiTier =
  | "full_verified"
  | "summary_verified_limited_transactions"
  | "review_required";

export interface BankStatementUiReadinessInput {
  /** Whether the printed summary money (opening/ending/credits/money-out) is verified. */
  summaryMetricsReady: ReadinessState;
  /** Whether the itemized transaction ledger reconciles to the printed totals. */
  transactionAnalyticsReady: ReadinessState;
  /** Whether a verified running/daily-balance chain is available. */
  runningBalanceAnalyticsReady: ReadinessState;
  /** Backend review flag — used only to keep messaging honest, NOT to pick the tier. */
  needsReview: boolean;
  /**
   * True only when an average balance can be shown WITHOUT estimating: either the
   * statement prints an average balance (captured with provenance) OR verified
   * daily/running-balance data exists to compute a real average. Never an
   * opening/ending estimate.
   */
  hasProvenAverageBalance: boolean;
  /**
   * Provenance of these readiness states — "backend" when persisted backend
   * metadata drove them, "legacy_inferred" when derived from needsReview + the
   * printed balance equation. Defaults to "legacy_inferred" when omitted.
   */
  source?: BankStatementReadinessSource;
}

export interface BankStatementUiShowFlags {
  summaryMetrics: boolean;
  cashFlowChart: boolean;
  transactionCount: boolean;
  categorySpendChart: boolean;
  merchantSpendChart: boolean;
  transactionAnalytics: boolean;
  averageBalance: boolean;
}

export interface BankStatementUiReadiness {
  tier: BankStatementUiTier;
  label: string;
  message: string;
  show: BankStatementUiShowFlags;
  /** Provenance of the decision — "backend" or "legacy_inferred". */
  source: BankStatementReadinessSource;
}

export const TIER_LABELS: Record<BankStatementUiTier, string> = {
  full_verified: "Verified Statement",
  summary_verified_limited_transactions: "Verified Summary",
  review_required: "Review Required",
};

export const TIER_MESSAGES: Record<BankStatementUiTier, string> = {
  full_verified:
    "The statement totals and transaction details were verified.",
  summary_verified_limited_transactions:
    "The statement totals were verified against the printed bank statement. Detailed transaction-level charts are limited because not every transaction could be fully verified.",
  review_required:
    "We could not fully verify the printed statement totals. Please review this statement before relying on the results.",
};

/**
 * Map existing readiness fields into the UI display tier.
 *
 * Tier rules (exact, spec §5):
 *   - summaryMetricsReady != ready                     => review_required (Tier 3)
 *   - summaryMetricsReady = ready & txnAnalytics ready => full_verified   (Tier 1)
 *   - summaryMetricsReady = ready & txnAnalytics !ready => summary_verified_limited_transactions (Tier 2)
 *
 * Critically, when `needsReview` is true ONLY because transaction analytics are
 * blocked while the summary is verified, this returns Tier 2 — never a scary full
 * review failure.
 */
export function deriveBankStatementUiReadiness(
  input: BankStatementUiReadinessInput,
): BankStatementUiReadiness {
  const summaryReady = input.summaryMetricsReady === "ready";
  const transactionReady = input.transactionAnalyticsReady === "ready";
  const source: BankStatementReadinessSource = input.source ?? "legacy_inferred";

  // Tier 3 — summary money is not verified. Hide everything chart-like.
  // NOTE: any non-"ready" state (blocked / not_available / unknown) lands here,
  // so backend `summaryMetricsReady=blocked` is NEVER upgraded by the frontend.
  if (!summaryReady) {
    return {
      tier: "review_required",
      label: TIER_LABELS.review_required,
      message: TIER_MESSAGES.review_required,
      show: {
        summaryMetrics: false,
        cashFlowChart: false,
        transactionCount: false,
        categorySpendChart: false,
        merchantSpendChart: false,
        transactionAnalytics: false,
        averageBalance: false,
      },
      source,
    };
  }

  // Tier 1 — summary verified AND transaction ledger verified. Full dashboard.
  if (transactionReady) {
    return {
      tier: "full_verified",
      label: TIER_LABELS.full_verified,
      message: TIER_MESSAGES.full_verified,
      show: {
        summaryMetrics: true,
        cashFlowChart: true,
        transactionCount: true,
        categorySpendChart: true,
        merchantSpendChart: true,
        transactionAnalytics: true,
        averageBalance: input.hasProvenAverageBalance,
      },
      source,
    };
  }

  // Tier 2 — summary verified, transaction analytics limited. Summary dashboard
  // only. No transaction-derived charts, no scary global failure banner.
  return {
    tier: "summary_verified_limited_transactions",
    label: TIER_LABELS.summary_verified_limited_transactions,
    message: TIER_MESSAGES.summary_verified_limited_transactions,
    show: {
      summaryMetrics: true,
      cashFlowChart: true,
      transactionCount: false,
      categorySpendChart: false,
      merchantSpendChart: false,
      transactionAnalytics: false,
      averageBalance: input.hasProvenAverageBalance,
    },
    source,
  };
}

// ─── Adapter: real frontend statement fields → readiness states ───────────────

/** Minimal structural shape of the fields the adapter reads (subset of BankStatement). */
export interface BankStatementReadinessFields {
  openingBalance: string | null;
  closingBalance: string | null;
  totalCredits: string | null;
  statedCredits: string | null;
  totalDebits: string | null;
  statedDebits: string | null;
  serviceFees: string | null;
  checksAmount: string | null;
  moneyOutOverride: string | null;
  avgDailyBalance: string | null;
  needsReview: boolean;
  transactionExtractionFailed: boolean;
  /**
   * Dealer-confirmed summary correction for a Review Required statement. When
   * `dealerVerifiedAt` is set, the dealer asserted the true headline totals, so
   * the summary is treated as verified (Tier 2) regardless of extraction state.
   */
  dealerCorrectedCredits?: string | null;
  dealerCorrectedDebits?: string | null;
  dealerCorrectedAvgBalance?: string | null;
  dealerVerifiedAt?: string | null;
  /**
   * Persisted backend readiness metadata (JSON string) from
   * `bank_statements.bankReadinessJson`. Present only on records extracted after
   * Phase 14C.2 on the resolved-candidate path; null/absent for legacy records.
   */
  bankReadinessJson?: string | null;
}

/**
 * Backend readiness metadata as persisted on `bank_statements.bankReadinessJson`.
 * Mirrors the server's `PersistedBankReadiness` shape. The three readiness states
 * are the authoritative backend decision and are used verbatim when present.
 */
export interface BackendReadinessMetadata {
  summaryMetricsReady: ReadinessState;
  transactionAnalyticsReady: ReadinessState;
  runningBalanceAnalyticsReady: ReadinessState;
  validationDecisionSource: string | null;
  reviewReasons: string[];
}

const READINESS_STATES: ReadonlySet<string> = new Set<ReadinessState>([
  "ready",
  "blocked",
  "not_available",
  "unknown",
]);

function asReadinessState(v: unknown): ReadinessState {
  return typeof v === "string" && READINESS_STATES.has(v)
    ? (v as ReadinessState)
    : "unknown";
}

/**
 * Defensively parse the persisted backend readiness metadata. Returns null when
 * the column is absent/empty/malformed so callers fall back to legacy inference.
 * Never throws.
 */
export function parseBankReadiness(
  raw: string | null | undefined,
): BackendReadinessMetadata | null {
  if (raw == null || raw === "") return null;
  let obj: unknown;
  try {
    obj = JSON.parse(raw);
  } catch {
    return null;
  }
  if (obj == null || typeof obj !== "object") return null;
  const o = obj as Record<string, unknown>;
  // Require at least the three readiness keys to be present to treat as backend truth.
  if (
    !("summaryMetricsReady" in o) &&
    !("transactionAnalyticsReady" in o) &&
    !("runningBalanceAnalyticsReady" in o)
  ) {
    return null;
  }
  const reviewReasons = Array.isArray(o.reviewReasons)
    ? o.reviewReasons.filter((r): r is string => typeof r === "string")
    : [];
  return {
    summaryMetricsReady: asReadinessState(o.summaryMetricsReady),
    transactionAnalyticsReady: asReadinessState(o.transactionAnalyticsReady),
    runningBalanceAnalyticsReady: asReadinessState(o.runningBalanceAnalyticsReady),
    validationDecisionSource:
      typeof o.validationDecisionSource === "string"
        ? o.validationDecisionSource
        : null,
    reviewReasons,
  };
}

/** Residual tolerance (dollars) when checking the printed balance equation. */
const BALANCE_TOLERANCE = 1.0;

function num(v: string | null | undefined): number | null {
  if (v == null) return null;
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * Authoritative headline money-out, mirroring the dashboard's `statedMoneyOut`:
 * a uniquely-resolved folded override when present, else withdrawals + checks + fees.
 */
function statedMoneyOut(s: BankStatementReadinessFields): number {
  const override = num(s.moneyOutOverride);
  if (override != null) return override;
  return (
    (num(s.statedDebits ?? s.totalDebits) ?? 0) +
    (num(s.checksAmount) ?? 0) +
    (num(s.serviceFees) ?? 0)
  );
}

/**
 * Reconstruct the readiness states from the fields the frontend actually has.
 *
 *  - summaryMetricsReady: ready when the backend did not flag the statement OR when
 *    the printed balance equation (opening + credits − money-out ≈ closing) closes
 *    within tolerance. A flagged statement whose summary still reconciles is the
 *    Chase/FFB case the spec targets — it must read as Tier 2, not Tier 3.
 *  - transactionAnalyticsReady: ready only when the summary is ready, extraction did
 *    not fail, transactions are present, and the statement is not flagged for review.
 *  - runningBalanceAnalyticsReady: not_available (the frontend cannot verify a chain).
 *  - hasProvenAverageBalance: a printed average exists, or the transaction ledger is
 *    verified (Tier 1) so a real day-by-day average can be computed. Never estimated.
 */
export function deriveReadinessInputFromStatement(
  s: BankStatementReadinessFields,
  opts: { hasTransactions: boolean },
): BankStatementUiReadinessInput {
  const printedAveragePresent = num(s.avgDailyBalance) != null;

  // Compute the AI/backend-derived readiness first; the dealer override (below) is
  // applied afterward and only ever upgrades a not-ready summary.
  const base: BankStatementUiReadinessInput = (() => {
    // ─── Backend truth path ───────────────────────────────────────────────────
    // When the backend persisted readiness metadata, it is authoritative. We use the
    // three readiness states verbatim — NO balance-equation override, NO needsReview
    // re-inference. Any non-"ready" state (blocked / not_available / unknown) is
    // honored as-is, so the frontend can never upgrade a backend-blocked statement.
    const backend = parseBankReadiness(s.bankReadinessJson);
    if (backend) {
      // hasProvenAverageBalance never estimates: a printed average, or a verified
      // running/daily-balance chain. Tier-1 transaction readiness alone is NOT enough.
      const hasProvenAverageBalance =
        printedAveragePresent || backend.runningBalanceAnalyticsReady === "ready";
      return {
        summaryMetricsReady: backend.summaryMetricsReady,
        transactionAnalyticsReady: backend.transactionAnalyticsReady,
        runningBalanceAnalyticsReady: backend.runningBalanceAnalyticsReady,
        needsReview: s.needsReview,
        hasProvenAverageBalance,
        source: "backend",
      };
    }

    // ─── Legacy inference fallback ─────────────────────────────────────────────
    // Only reached for records with no persisted backend readiness (pre-Phase-14C.2).
    const opening = num(s.openingBalance);
    const closing = num(s.closingBalance);
    const credits = num(s.statedCredits ?? s.totalCredits);
    const moneyOut = statedMoneyOut(s);

    const canCheckEquation = opening != null && closing != null && credits != null;
    const summaryReconciles =
      canCheckEquation &&
      Math.abs(opening + credits - moneyOut - closing) <= BALANCE_TOLERANCE;

    // Never DOWNGRADE a statement the backend already accepted: !needsReview ⇒ ready.
    // Only when flagged do we require the printed equation to close to claim "verified".
    const summaryReady = !s.needsReview || summaryReconciles;

    const transactionsBlocked =
      s.transactionExtractionFailed || s.needsReview || !opts.hasTransactions;
    const transactionAnalyticsReady: ReadinessState =
      summaryReady && !transactionsBlocked ? "ready" : "blocked";

    // Legacy records have no verified running-balance chain (runningBalance is always
    // not_available here), so the proven-average gate reduces to a printed average
    // only — never a transaction-derived estimate.
    const hasProvenAverageBalance = printedAveragePresent;

    return {
      summaryMetricsReady: summaryReady ? "ready" : "blocked",
      transactionAnalyticsReady,
      runningBalanceAnalyticsReady: "not_available" as ReadinessState,
      needsReview: s.needsReview,
      hasProvenAverageBalance,
      source: "legacy_inferred" as BankStatementReadinessSource,
    };
  })();

  // ─── Dealer-confirmed summary override (UPGRADE-ONLY) ────────────────────────
  // When the dealer manually confirmed the headline totals for a statement whose
  // summary the AI could NOT verify, rescue it to Tier 2: trust the summary money,
  // but keep transaction-level analytics blocked (the ledger was never verified).
  //
  // This is deliberately upgrade-only — it applies ONLY when the AI/backend summary
  // is not already ready. Consequences:
  //   • A later re-extraction that succeeds (summary becomes ready) regains Tier 1;
  //     the stale dealer correction no longer governs the tier.
  //   • An already-verified statement can never be downgraded to Tier 2 by a stray
  //     or overreaching correction write.
  if (s.dealerVerifiedAt && base.summaryMetricsReady !== "ready") {
    const correctedAvgPresent = num(s.dealerCorrectedAvgBalance ?? null) != null;
    return {
      summaryMetricsReady: "ready",
      transactionAnalyticsReady: "blocked",
      runningBalanceAnalyticsReady: "not_available",
      needsReview: s.needsReview,
      hasProvenAverageBalance: correctedAvgPresent || printedAveragePresent,
      source: "dealer_verified",
    };
  }

  return base;
}
